<script setup lang="ts">
import {
    faBasketShopping,
    faCheckDouble,
    faFilterList,
    faUserGroup,
} from '@fortawesome/pro-regular-svg-icons';
import Header from './_components/ReserveFlowHeader.vue';
import ReserveFlowStepper, {
    type StepperStep,
} from './_components/ReserveFlowStepper.vue';
import { ReserveFlowProviderKey } from './constant';
import { NuxtPage } from '#components';
import WarningModal from '@components/ui/reserve&reserve-flow/WarningModal.vue';

const { isMrbilit } = useWhiteLabel();
const route = useRoute();
const router = useRouter();
const isMounted = useMounted();
const autoReserveStore = useAutoReserveStore();

const { open } = useModal({
    component: WarningModal,
});

const isAutoReserve = computed(
    () => autoReserveStore.isAutoReserve || route.path.includes('/reserve/auto')
);
const stepper = (index?: number) => {
    // form have its own step guard that would clash with auto reserve guard
    // should add isAutoReserve condition later if the layout used for all services
    if (index !== undefined) {
        router.go(index - currentStep.value);
    } else {
        router.back();
    }
};

const back = () => {
    approval(() => {
        stepper();
    });
};

const approval = async (callback: (args?: any) => void) => {
    const result = await open({
        service: 'terminate',
    });
    if (result.args?.[0]) {
        callback();
    }
};
const currentStep = computed({
    get(): number {
        return steps.value.findIndex(step => step.page === currentPage.value);
    },
    set(newValue: number) {
        if (newValue < 0) {
            autoReserveStore.resetAutoReserve(true);
        }
        approval(() => stepper(newValue));
    },
});

provide(ReserveFlowProviderKey, {
    stepper,
    currentStep,
});

const currentPage = computed(() => {
    if (route.path.includes('/reserve/raise')) {
        return 'raise';
    }
    if (route.path.includes('/reserve/prepay')) {
        return 'prepay';
    }
    if (route.path.includes('/reserve/auto-view')) {
        return 'auto';
    }
    if (route.path === '/reserve/auto-reserve') {
        return 'auto-reserve';
    }
    if (route.path.includes('/reserve/form')) {
        return 'pax';
    }
    if (route.path.includes('/reserve/auto-reserve/filters')) {
        return 'filters';
    }
    return 'floating';
});

const steps = computed<
    (StepperStep & {
        page: string;
    })[]
>(() =>
    [
        {
            page: 'auto-reserve',
            icon: faBasketShopping,
            title: 'تعیین بلیط',
            mobileTitle: 'بلیط',
            pageHeaderTitle: 'تعیین بلیط‌های دلخواه',
            url: '/reserve/auto-reserve',
            condition: !isMounted.value || isAutoReserve.value,
        },
        {
            page: 'filters',
            icon: faFilterList,
            title: 'تعیین شرایط',
            mobileTitle: 'شرایط',
            pageHeaderTitle: 'تعیین شرایط بلیط',
            url: '/reserve/auto-reserve/filters',
            condition: !isMounted.value || isAutoReserve.value,
        },
        {
            page: 'pax',
            icon: faUserGroup,
            title: 'اطلاعات مسافران',
            mobileTitle: 'مسافران',
            pageHeaderTitle: 'ورود اطلاعات مسافران',
            url: '/reserve/form',
            condition: !isMounted.value || isAutoReserve.value,
        },
        {
            page: 'prepay',
            icon: faCheckDouble,
            title: 'تایید اطلاعات',
            mobileTitle: 'تایید اطلاعات',
            pageHeaderTitle: 'تایید اطلاعات',
            url: '/reserve/prepay',
            condition: !isMounted.value || isAutoReserve.value,
        },
    ].filter(item => item.condition)
);

onBeforeRouteLeave((to, from, next) => {
    if (to.path === from.path) {
        next();
        return;
    }
    if (!to.path.startsWith('/reserve') && !from.path.includes('/form')) {
        if (isAutoReserve.value) {
            setTimeout(() => {
                autoReserveStore.resetAutoReserve(true);
            }, 500);
        }
    }
    next();
});

const currentPageStep = computed(() => steps.value?.[currentStep.value]);
</script>

<template>
    <div>
        <Header :title="currentPageStep?.pageHeaderTitle || ''" @back="back">
            <ReserveFlowStepper v-model="currentStep" class="stepper" :steps />
        </Header>
        <NuxtPage />
        <VitePwaManifest v-if="isMrbilit" />
    </div>
</template>

<style lang="scss" scoped>
.stepper {
    max-width: 700px;
    margin-inline: auto;
}
</style>
